import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { Box, Grid } from '@mui/material';
import { Edit } from '@styled-icons/boxicons-solid';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';
import { CardIllustration, EmptyStateCard, Tabs, ThemeButton, ThemeTypography } from 'DesignSystem';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ComponentItemTable from 'FeatureModules/ComponentChain/Components/ComponentItemsTable';
import useComponentChain from 'FeatureModules/ComponentChain/hooks/useComponentChain';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import {
  ECOMPONENT_CHAIN_COLUMN_IDS,
  EComponentItemTableColumnKeys,
} from 'FeatureModules/ComponentChain/utils/headerColumns';
import ProductSupplyChainTable from 'FeatureModules/ProductPage/Components/Tables/ProductSupplyChainTable';
import { GET_CHAINS_INCLUDING_CHAIN } from 'graphql/secure/queries/chain.queries';
import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Booleanish } from 'types/booleanish.types';
import ComponentChainTable from '../Components/Table/ComponentChainTable';
import { ITableRowAction } from '../Components/Table/TableRow';
import { IChainWithDepth } from '../types/chain.types';

const StyledSubTitle = styled(ThemeTypography)(({ theme }) => ({
  textTransform: 'uppercase',
}));

enum IComponentOverviewTabs {
  COMPONENT_CHAINS = 'COMPONENT_CHAINS',
  PRODUCT_SUPPLY_CHAINS = 'PRODUCT_SUPPLY_CHAINS',
}

const COMPONENT_OVERVIEW_TABS: ITab[] = [
  {
    key: IComponentOverviewTabs.COMPONENT_CHAINS,
    label: 'Component chains',
    startIconName: 'component-chain',
  },
  {
    key: IComponentOverviewTabs.PRODUCT_SUPPLY_CHAINS,
    label: 'Product supply chains',
    startIconName: 'supply-chain',
  },
];

const ComponentChainOverview: FC = () => {
  const { componentChain, loading, error, handleEditComponentChain } = useComponentChain();
  const navigate = useNavigate();

  const { data, loading: includedChainsLoading } = useQuery<{
    chainsIncludingChain: IChainWithDepth[];
  }>(GET_CHAINS_INCLUDING_CHAIN, {
    variables: { id: componentChain?.id },
    skip: !componentChain?.id,
  });

  const [currentTab, setCurrentTab] = useState<IComponentOverviewTabs>(
    IComponentOverviewTabs.COMPONENT_CHAINS
  );

  const { componentChainsIncludingThisChain, supplyChainsIncludingThisChain } = useMemo(() => {
    const chainsIncludingThisChain = data?.chainsIncludingChain || [];
    return {
      componentChainsIncludingThisChain: chainsIncludingThisChain.filter(
        chain => !chain.chain.productId
      ),
      supplyChainsIncludingThisChain: chainsIncludingThisChain.filter(
        chain => !!chain.chain.productId
      ),
    };
  }, [data]);

  const componentTableActions: ITableRowAction[] = [
    {
      renderIcon: () => <Edit size="17" />,
      tooltip: 'Edit component chain',
      onClick: (id: string) => navigate(`/component-chains/${id}/editor`),
    },
  ];

  const supplyChainTableActions: ITableRowAction[] = [
    {
      renderIcon: () => <Edit size="17" />,
      tooltip: 'Edit supply chain',
      onClick: (chainId: string) => {
        const productId = supplyChainsIncludingThisChain?.find(({ chain }) => chain.id === chainId)
          ?.chain?.productId;
        navigate(`/supply-chains/active/${productId}/${chainId}/editor`);
      },
    },
  ];

  if (loading || includedChainsLoading) {
    return <Loader />;
  }
  if (error || !componentChain) {
    return <ErrorState />;
  }

  const handleChangeTab = (tab: string) => {
    setCurrentTab(tab as IComponentOverviewTabs);
  };

  const handleMappingClick = () => {
    navigate(`editor`);
  };

  const handleComponentRowClick = (component: IComponentItem) => {
    navigate(`/components/${component.id}`);
  };

  const handleClickPartner = (partnerId: string) => {
    navigate(`/partners/${partnerId}`);
  };

  const handleComponentChainClick = (chainId: string) => {
    navigate(`/component-chains/${chainId}`);
  };

  const handleSupplyChainClick = (productId: string, chainId: string) => {
    navigate(`/supply-chains/active/${productId}/${chainId}`);
  };

  return (
    <PageContainer>
      <PageTitle
        title="Component chain overview"
        titleExtension={componentChain.title}
        enableAutoGoBackHistoryButton
        goBackLabel="All component chains"
        goBackUrl="/component-chains"
      />
      <Box mt={4}>
        <OverviewHeader image={componentChain.image} placeholderIconName="chain-placeholder">
          <FlexBox alignContent="center" mb={1}>
            <ThemeTypography variant="TITLE_MEDIUM">{componentChain.title}</ThemeTypography>
          </FlexBox>

          <FlexBox mb={2} gap={1}>
            {!!componentChain.outputComponents?.length && (
              <FlexBox gap={0.5}>
                <Icon name="raw-material" color="gray-80" />
                <ThemeTypography
                  variant="BODY_MEDIUM_BOLD"
                  color="GRAY_80"
                  maxWidth={300}
                  autoOverflow
                >
                  {componentChain.outputComponents
                    .flatMap(({ rawMaterials }) => rawMaterials)
                    .reduce(
                      (prev, rawMaterial) =>
                        prev ? `${prev}, ${rawMaterial.title}` : rawMaterial.title,
                      ''
                    )}
                </ThemeTypography>
              </FlexBox>
            )}

            {componentChain.ownedBy && (
              <FlexBox
                gap={0.5}
                clickable={Booleanish(true)}
                onClick={() =>
                  componentChain.ownedBy?.id && handleClickPartner(componentChain.ownedBy.id)
                }
              >
                <Image
                  image={componentChain.ownedBy?.logo}
                  backupIcon="partner"
                  width={12}
                  height={12}
                />
                <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                  {componentChain.ownedBy.name}
                </ThemeTypography>
              </FlexBox>
            )}
          </FlexBox>

          <FlexBox gap={2}>
            {(!!componentChainsIncludingThisChain?.length ||
              !!supplyChainsIncludingThisChain?.length) && (
              <ThemeButton
                startIcon={<Icon name="component-chain-edit" />}
                size="large"
                color="YELLOW"
                loading={false}
                onClick={handleMappingClick}
              >
                Map component chain
              </ThemeButton>
            )}

            <ThemeButton
              startIcon={<Icon name="edit" />}
              size="large"
              color="BLUE_ICE"
              loading={false}
              onClick={() => handleEditComponentChain(componentChain)}
            >
              Edit component chain
            </ThemeButton>
          </FlexBox>
        </OverviewHeader>
      </Box>

      {!!componentChain.outputComponents?.length && (
        <Box flex={1} mt={4}>
          <PageSubTitle title="Component that outputs from this chain" />
          <Box>
            <ComponentItemTable
              hideHeader
              hideDelete
              components={componentChain.outputComponents}
              columns={[
                EComponentItemTableColumnKeys.TITLE,
                EComponentItemTableColumnKeys.PARTNER,
                EComponentItemTableColumnKeys.MODIFIED_TIMESTAMP,
                EComponentItemTableColumnKeys.ACTIONS,
              ]}
              onSelect={handleComponentRowClick}
            />
          </Box>
        </Box>
      )}

      <FlexBox gap={2} flexDirection="column" alignItems="normal">
        {(!!componentChainsIncludingThisChain.length ||
          !!supplyChainsIncludingThisChain.length) && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box mt={4}>
                <StyledSubTitle variant="TITLE_EXTRA_SMALL">
                  Other chains that include this component chain
                </StyledSubTitle>
              </Box>
              {componentChainsIncludingThisChain?.length &&
              supplyChainsIncludingThisChain?.length ? (
                <>
                  <Box mt={1.5} mb={1} display="flex" justifyContent="space-between">
                    <Tabs
                      tabs={COMPONENT_OVERVIEW_TABS}
                      selectedTab={currentTab}
                      onChange={handleChangeTab}
                    />
                  </Box>
                  {currentTab === IComponentOverviewTabs.COMPONENT_CHAINS ? (
                    <ComponentChainTable
                      columnKeys={[
                        ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
                        ECOMPONENT_CHAIN_COLUMN_IDS.ACTIONS,
                      ]}
                      componentChains={componentChainsIncludingThisChain}
                      actions={componentTableActions}
                      onRowClick={handleComponentChainClick}
                    />
                  ) : (
                    <ProductSupplyChainTable
                      productSupplyChains={supplyChainsIncludingThisChain}
                      actions={supplyChainTableActions}
                      onRowClick={handleSupplyChainClick}
                    />
                  )}
                </>
              ) : (
                <>
                  {!!componentChainsIncludingThisChain?.length && (
                    <ComponentChainTable
                      columnKeys={[
                        ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
                        ECOMPONENT_CHAIN_COLUMN_IDS.ACTIONS,
                      ]}
                      componentChains={componentChainsIncludingThisChain}
                      actions={componentTableActions}
                      onRowClick={handleComponentChainClick}
                    />
                  )}
                  {!!supplyChainsIncludingThisChain?.length && (
                    <ProductSupplyChainTable
                      productSupplyChains={supplyChainsIncludingThisChain}
                      actions={supplyChainTableActions}
                      onRowClick={handleSupplyChainClick}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        )}

        {!componentChainsIncludingThisChain?.length && !supplyChainsIncludingThisChain?.length && (
          <Box mt={4}>
            <EmptyStateCard
              title="Map this component chain and re-use it in your different product’s supply chains"
              variant={EItemTypes.CHAIN_MAPPING}
              buttonIcon={<Icon name="component-chain-edit" />}
              buttonText="Map component chain"
              background={EBackgroundTypes.WORLD}
              size={EAvailableSizes.MEDIUM}
              renderIllustration={
                <CardIllustration
                  image={EIllustrationTypes.LOCATIONS}
                  width={300}
                  right={25}
                  top={0}
                />
              }
              onCreateItemClick={handleMappingClick}
            />
          </Box>
        )}
      </FlexBox>
    </PageContainer>
  );
};

export default ComponentChainOverview;
